/**
 * Helpers for CHARGEBACK_REASON_CODE_RESOLVED / RESPONSE_POSTURE on the case screen.
 */

export const RESPONSE_POSTURE = {
  SUBMIT_DOCUMENTS: 'SUBMIT_DOCUMENTS',
  REFUND_PROOF_ONLY: 'REFUND_PROOF_ONLY',
  ACCEPT_LIABILITY: 'ACCEPT_LIABILITY',
  NOT_A_DISPUTE: 'NOT_A_DISPUTE',
};

export const DEFAULT_VERTICAL = 'Hospitality';

const normalize = (value) => String(value ?? '').trim().toUpperCase();

export const normalizeResponsePosture = (value) => {
  const posture = normalize(value);
  // Empty / unknown → treat like a normal dispute.
  if (!posture) return RESPONSE_POSTURE.SUBMIT_DOCUMENTS;
  return posture;
};

export const shouldShowDocumentChecklist = (responsePosture) => {
  const posture = normalizeResponsePosture(responsePosture);
  return (
    posture === RESPONSE_POSTURE.SUBMIT_DOCUMENTS ||
    posture === RESPONSE_POSTURE.REFUND_PROOF_ONLY
  );
};

export const isRefundProofOnly = (responsePosture) =>
  normalizeResponsePosture(responsePosture) === RESPONSE_POSTURE.REFUND_PROOF_ONLY;

/** Cannot be fought / nothing at stake — hide Fight actions. */
export const isNonActionablePosture = (responsePosture) => {
  const posture = normalizeResponsePosture(responsePosture);
  return (
    posture === RESPONSE_POSTURE.ACCEPT_LIABILITY ||
    posture === RESPONSE_POSTURE.NOT_A_DISPUTE
  );
};

export const isNotADisputePosture = (responsePosture) =>
  normalizeResponsePosture(responsePosture) === RESPONSE_POSTURE.NOT_A_DISPUTE;

export const isAcceptLiabilityPosture = (responsePosture) =>
  normalizeResponsePosture(responsePosture) === RESPONSE_POSTURE.ACCEPT_LIABILITY;

export const formatCategoryLabel = (category, subcategory) => {
  const cat = category != null ? String(category).trim() : '';
  const sub = subcategory != null ? String(subcategory).trim() : '';
  if (cat && sub) return `${cat} · ${sub}`;
  return cat || sub || '';
};

export const isComplianceCategory = (category) =>
  normalize(category) === 'COMPLIANCE & REGULATORY';

export const isProvisionalWinValue = (winLoss) =>
  normalize(winLoss).includes('PROVISIONAL');

export const formatProvisionalWinLabel = (winLoss, provisionalUntil, formatDateFn) => {
  if (!isProvisionalWinValue(winLoss)) {
    return winLoss != null && String(winLoss).trim() ? String(winLoss).trim() : '';
  }
  const until = provisionalUntil
    ? typeof formatDateFn === 'function'
      ? formatDateFn(new Date(provisionalUntil))
      : String(provisionalUntil).slice(0, 10)
    : null;
  return until ? `Provisional Win, final on ${until}` : 'Provisional Win';
};

/**
 * Display processor due date and Harmonate estimate separately.
 * Never swaps the estimate into the processor slot.
 */
export const formatDueDateDisplay = (row, formatDateFn) => {
  const processorRaw = row?.DUE_DATE ?? row?.due_date ?? null;
  const estimateRaw =
    row?.HARMONATE_ESTIMATED_DUE_DATE ?? row?.harmonate_estimated_due_date ?? null;
  const confidence =
    row?.DUE_DATE_ESTIMATE_CONFIDENCE ?? row?.due_date_estimate_confidence ?? null;

  const fmt = (raw) => {
    if (raw == null || raw === '') return null;
    try {
      return typeof formatDateFn === 'function'
        ? formatDateFn(new Date(raw))
        : String(raw).slice(0, 10);
    } catch {
      return String(raw).slice(0, 10);
    }
  };

  const processorDate = fmt(processorRaw);
  const estimateDate = fmt(estimateRaw);

  if (processorDate) {
    return {
      text: processorDate,
      isEstimate: false,
      confidence: null,
      processorDate,
      estimateDate,
    };
  }

  if (estimateDate) {
    return {
      text: `No due date from processor · Harmonate estimate: ${estimateDate}`,
      isEstimate: true,
      confidence: confidence != null ? String(confidence).trim() : null,
      processorDate: null,
      estimateDate,
    };
  }

  return {
    text: 'No due date from processor',
    isEstimate: false,
    confidence: null,
    processorDate: null,
    estimateDate: null,
  };
};

export const emptyReasonCodeResolved = () => ({
  found: false,
  reason_code_key: null,
  vertical: null,
  resolved_reason_code: null,
  resolved_reason: null,
  harmonate_category: null,
  harmonate_subcategory: null,
  posture_note: null,
  response_posture: null,
  case_type: null,
  suggested_documents: {},
});
